/**
 * MyWeekBand — "My week", the top band of the Clients home page for any
 * viewer whose own roster row is does_spice: a SPICE-only therapist, or
 * a dual-role viewer who also does care management. The care-manager
 * home is otherwise unchanged; this band renders only when the
 * aggregate's `my_week` is present AND — for a viewer who is not
 * SPICE-only — carries something to show (ClientsHome.tsx's own gate).
 *
 * Days render Monday..Friday, and Saturday or Sunday when a visit falls
 * on it, TODAY FIRST (src/lib/myWeek.ts orderMyWeekDays, pure and
 * unit-tested). Each line: the kind's dot in the kind's colour (the
 * kind named to a screen reader only), the session's 12-hour time, the
 * client, a Due/Visited chip, the last plan (until a confirmed
 * next-plan proposal exists to replace it — see cmHome.ts), a "Write
 * the SPICE email summary" door (write_clinical_notes gate, ClientsBand's
 * own canWriteClinical prop) into the SPICE email drafter with the client
 * and the last plan carried along (spiceVisitPrefill.ts) — the email is
 * the visit note, filed from there — and, for a viewer who may change a
 * client's schedule (canEditSchedule), a small "Edit schedule" link
 * opening the same schedule editor the client record uses. A line
 * clinician reads her week and changes none of its schedule.
 *
 * A legend under the heading says which colour is which, whenever the
 * week has something in it.
 *
 * Clients with no visit of this therapist's all week, because another
 * therapist covers them or they are paused, appear ONCE, under "Not this
 * week", with the away phrase ("Covered by Amara Lindenbrook until 14
 * Oct", "Paused until 14 Oct (Vacation)"), never per weekday. Times and
 * away phrases read through the shared SPICE copy
 * (src/lib/spiceScheduleCopy.ts).
 */

import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { SpiceKindDot, SpiceKindLegend } from '@/components/clientRecord/SpiceKindMark';
import { Chip } from '@/components/primitives/Chip';
import { TextAction } from '@/components/workspace/TextAction';
import { NavArrowLink } from '@/components/home/NavArrowLink';
import type { CmHomeMyWeekWire } from '@/lib/cmHomeApi';
import { myWeekIsEmpty, myWeekdayName, orderMyWeekDays } from '@/lib/myWeek';
import { SPICE_KIND_LABELS, formatSpiceAwayOfStatus, formatSpiceTime } from '@/lib/spiceScheduleCopy';
import { MyWeekEditScheduleDialog } from '@/components/home/MyWeekEditScheduleDialog';
import { writeSpiceVisitPrefill } from '@/lib/spiceVisitPrefill';

export interface MyWeekBandProps {
  myWeek: CmHomeMyWeekWire;
  todayIso: string;
  tenantSlug: string;
  /** write_clinical_notes — the same write-intent gate ClientsBand's
   *  "+ Case Note" uses; a read-only viewer sees the week but no note door. */
  canWriteClinical: boolean;
  /** canEditClientSchedule (manage_client_identity) — "Edit schedule"
   *  renders only for a viewer who may change a client's schedule. */
  canEditSchedule: boolean;
  /** "This week's grid →" (Team › SPICE week) renders only for a
   *  viewer canViewStaffTools admits to the Team page at all. */
  showSpiceWeekGridLink: boolean;
  /** "All SPICE clients →" (All clients, SPICE view) renders only for a
   *  viewer the roster page admits (canViewAnyClientRoster: the office
   *  width, or the engagement lane with a roster flag) — the same gate
   *  ClientsBand's own "All clients" link uses. */
  showAllSpiceClientsLink: boolean;
}

export function MyWeekBand({
  myWeek,
  todayIso,
  tenantSlug,
  canWriteClinical,
  canEditSchedule,
  showSpiceWeekGridLink,
  showAllSpiceClientsLink,
}: MyWeekBandProps): ReactNode {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<{ clientId: string; date: string } | null>(null);
  const orderedDays = orderMyWeekDays(myWeek, todayIso);
  const isEmpty = myWeekIsEmpty(myWeek);

  return (
    <section aria-labelledby="my-week-heading" data-testid="my-week-band">
      <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="my-week-heading" className="flex h-7 items-center font-display text-lg leading-7 text-sage-800">
          My Week
        </h2>
        {/* Two quiet links, hidden for a viewer who can't open the page
            they point to — never a dead link. */}
        {(showSpiceWeekGridLink || showAllSpiceClientsLink) && (
          <div className="flex items-center gap-4">
            {showSpiceWeekGridLink && (
              <NavArrowLink to={`/t/${tenantSlug}/team?tab=spice_week`} data-testid="my-week-grid-link">
                This week&rsquo;s grid
              </NavArrowLink>
            )}
            {showAllSpiceClientsLink && (
              <NavArrowLink
                to={`/t/${tenantSlug}/clients/all?view=spice`}
                data-testid="my-week-all-spice-clients-link"
              >
                All SPICE clients
              </NavArrowLink>
            )}
          </div>
        )}
      </div>
      {!isEmpty && (
        <div className="-mt-2 mb-3.5">
          <SpiceKindLegend testId="my-week-legend" />
        </div>
      )}

      {isEmpty ? (
        <div
          data-testid="my-week-empty"
          className="py-2.5 text-sm text-ink-soft"
        >
          Your week is clear.
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {orderedDays.map((day) => (
            <div key={day.date} data-testid="my-week-day" data-today={day.is_today}>
              <h3 className="mb-1.5 text-label font-semibold uppercase tracking-label text-sage-400">
                {day.is_today ? 'Today' : myWeekdayName(day.weekday)}
              </h3>
              {day.visits.length === 0 ? (
                <p className="px-1 text-sm text-ink-soft" data-testid="my-week-day-empty">
                  Your day is clear.
                </p>
              ) : (
                <div className="border-t border-sage-100">
                  <ul className="m-0 list-none divide-y divide-sage-100 p-0">
                    {day.visits.map((v) => (
                      <li
                        key={`${day.date}:${v.client_id}`}
                        data-testid="my-week-visit"
                        className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-value font-semibold leading-tight text-ink">
                            <SpiceKindDot kind={v.kind} className="mr-1.5 align-middle" />
                            <span className="sr-only">{`${SPICE_KIND_LABELS[v.kind]} session, `}</span>
                            {formatSpiceTime(v.start_time)} · {v.client_display_name}
                          </p>
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-soft">
                            <Chip
                              variant={v.state === 'visited' ? 'affirm' : 'neutral'}
                              data-testid="my-week-visit-state"
                            >
                              {v.state === 'visited' ? 'Visited' : 'Due'}
                            </Chip>
                            <span data-testid="my-week-last-plan">
                              {v.last_plan ? `Last plan: ${v.last_plan}` : 'No plan on file yet'}
                            </span>
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          {canWriteClinical && (
                            <Button
                              // The SPICE accent role: this band renders
                              // only for a SPICE-only viewer's Home, so its
                              // one action always takes pepper.
                              variant="pepper"
                              size="sm"
                              data-testid="my-week-case-note"
                              onClick={() => {
                                // My week carries no decrypted contact
                                // fact (cmHome.ts decrypts nothing) — the
                                // Email helper's recipient field opens
                                // blank, same as today.
                                writeSpiceVisitPrefill(tenantSlug, v.client_id, {
                                  client_display_name: v.client_display_name,
                                  last_plan: v.last_plan,
                                  contact_first_name: null,
                                });
                                navigate(`/t/${tenantSlug}/ai-tools/email?client_id=${v.client_id}`);
                              }}
                            >
                              Write the SPICE email summary
                            </Button>
                          )}
                          {canEditSchedule && (
                            <TextAction
                              data-testid="my-week-edit-schedule"
                              onClick={() => setEditing({ clientId: v.client_id, date: day.date })}
                            >
                              Edit schedule
                            </TextAction>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}

          {myWeek.not_this_week.length > 0 && (
            <div data-testid="my-week-not-this-week">
              <h3 className="mb-1.5 text-label font-semibold uppercase tracking-label text-sage-400">
                Not this week
              </h3>
              <ul className="m-0 list-none space-y-1 p-0">
                {myWeek.not_this_week.map((entry) => (
                  <li
                    key={entry.client_id}
                    data-testid="my-week-not-this-week-entry"
                    className="text-sm text-ink-soft"
                  >
                    <span className="font-semibold text-ink">{entry.client_display_name}</span>
                    {' · '}
                    {formatSpiceAwayOfStatus(entry, todayIso)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {canEditSchedule && editing !== null && (
        <MyWeekEditScheduleDialog
          tenantSlug={tenantSlug}
          clientId={editing.clientId}
          initialDate={editing.date}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}
